import { Button } from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { SheetTitle } from "@/components/ui/sheet";
import AppInputNew from "@/components/Core/AppInput/AppInputNew";
import FilterSheetFrame from "./FilterSheetFrame";

interface Props<TDef> {
	open: boolean;
	onClose: () => void;
	/** Whether we're creating a brand-new filter (vs. editing an existing one). */
	isNew: boolean;
	initialName: string;
	initialDefinition: TDef;
	/** Commit the edited filter (name + definition) back to the draft list. */
	onSave: (name: string, definition: TDef) => void;
	/** Renders the domain's filter controls bound to the local draft definition. */
	renderBuilder: (definition: TDef, onChange: (def: TDef) => void) => React.ReactNode;
}

/**
 * Sidebar for defining a single named filter (an org preset). Reuses the shared
 * sheet frame and adds a name field on top of a domain-supplied builder. Edits
 * are kept local and only committed to the parent draft on "Done" so closing the
 * sheet discards an in-progress edit. Generic over the definition type.
 */
function FilterEditSheet<TDef>({
	open,
	onClose,
	isNew,
	initialName,
	initialDefinition,
	onSave,
	renderBuilder,
}: Props<TDef>) {
	const { t } = useTranslation();
	const [name, setName] = useState(initialName);
	const [definition, setDefinition] = useState<TDef>(initialDefinition);

	// Seed the local draft each time the sheet opens. Intentionally keyed on
	// `open` only — we don't want to clobber in-progress edits when the parent
	// re-renders with the same target.
	useEffect(() => {
		if (open) {
			setName(initialName);
			setDefinition(initialDefinition);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	const canSave = !!name.trim();

	const footer = (
		<div className="flex justify-end">
			<Button
				size="sm"
				bg="#333333"
				color="white"
				_hover={{ bg: "#444444" }}
				isDisabled={!canSave}
				onClick={() => onSave(name.trim(), definition)}
			>
				{t("DONE" as any)}
			</Button>
		</div>
	);

	return (
		<FilterSheetFrame
			open={open}
			onClose={onClose}
			header={<SheetTitle>{isNew ? t("NEW_FILTER" as any) : t("EDIT_FILTER" as any)}</SheetTitle>}
			footer={footer}
		>
			<div className="mb-5 px-5 pt-5">
				<AppInputNew
					label="FILTER_NAME"
					placeholder="FILTER_NAME"
					value={name}
					onChangeText={setName}
					mandatory
				/>
			</div>
			{renderBuilder(definition, setDefinition)}
		</FilterSheetFrame>
	);
}

export default FilterEditSheet;
